import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, RotateCcw, Send, User, MapPin } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { staffAPI, API_BASE_URL } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { ROLE_NAMES } from '../../constants/modules';

export default function SelfieCapture() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedBlob, setCapturedBlob] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [facingMode, setFacingMode] = useState('user');
  const [deviceLocation, setDeviceLocation] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [linkedStaff, setLinkedStaff] = useState(null);

  const canProxyCheckIn = useMemo(() => {
    if (!user) return false;
    if (user.role_name === ROLE_NAMES.SUPER_ADMIN) return true;
    return user.role_name === ROLE_NAMES.ADMIN || user.role_name === ROLE_NAMES.MANAGER;
  }, [user]);

  useEffect(() => {
    if (canProxyCheckIn) {
      staffAPI.list({ status: 'ACTIVE' }).then((res) => {
        setStaffList(res.data || []);
      }).catch(() => {});
    } else {
      staffAPI.getLinkedSelf().then((res) => {
        if (res?.data) {
          setLinkedStaff(res.data);
          setSelectedStaff(res.data.id);
          setStaffList([res.data]);
        }
      }).catch(() => {});
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setDeviceLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => setLocationError('Location access denied. Geofence check will fail.'),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setLocationError('Geolocation not supported by this browser.');
    }
  }, [canProxyCheckIn]);

  const startCamera = useCallback(async () => {
    setCameraError('');
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraActive(true);
    } catch (err) {
      setCameraError('Camera access denied. Please allow camera permissions.');
      setCameraActive(false);
    }
  }, [facingMode]);

  useEffect(() => {
    startCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, [startCamera]);

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
    setCapturedImage(dataUrl);

    canvas.toBlob((blob) => setCapturedBlob(blob), 'image/jpeg', 0.95);
  };

  const retake = () => {
    setCapturedImage(null);
    setCapturedBlob(null);
    startCamera();
  };

  const submitPhoto = async () => {
    if (!selectedStaff || !capturedBlob) return;
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('photo', capturedBlob, 'selfie.jpg');
      formData.append('staff_id', selectedStaff);
      if (deviceLocation) {
        formData.append('latitude', deviceLocation.latitude);
        formData.append('longitude', deviceLocation.longitude);
      }

      const token = localStorage.getItem('auth_token');
      const facilityId = localStorage.getItem('active_facility_id');

      const response = await fetch(`${API_BASE_URL}/selfie-attendance/upload`, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
          ...(facilityId && { 'X-Facility-Id': facilityId }),
        },
        body: formData,
      });

      const data = await response.json();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }

      navigate('/dashboard/selfie-attendance/confirmation', {
        state: {
          result: data,
          photoPreview: capturedImage,
          staffName: staffList.find((s) => s.id === selectedStaff)?.name || '',
        },
      });
    } catch (err) {
      alert(err.message || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <DashboardLayout>
      <div className="max-w-lg mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
            <Camera className="h-7 w-7 text-teal-600" />
            Selfie Attendance
          </h1>
          <p className="text-ga-text-muted mt-1">{today}</p>
        </div>

        <div className="ga-card rounded-xl shadow-sm border p-4 space-y-4">
          {canProxyCheckIn ? (
            <div>
              <label className="block text-sm font-medium text-ga-text-primary mb-1">Select Staff</label>
              <select
                value={selectedStaff}
                onChange={(e) => setSelectedStaff(e.target.value)}
                className="ga-input w-full px-3 py-2 rounded-lg"
              >
                <option value="">-- Select staff member --</option>
                {staffList.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
                ))}
              </select>
            </div>
          ) : linkedStaff ? (
            <div className="flex items-center gap-3 rounded-lg border border-ga-border-subtle bg-ga-bg-page/50 px-4 py-3">
              <User className="h-5 w-5 text-ga-primary" />
              <div>
                <p className="text-sm font-medium text-ga-text-primary">Checking in as</p>
                <p className="text-sm text-ga-text-muted">{linkedStaff.name}</p>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-ga-warning/30 bg-ga-warning/10 px-4 py-3 text-sm text-ga-text-primary">
              No employee profile is linked to your account. Ask an admin to enable app access when adding you as staff.
            </div>
          )}

          {locationError && (
            <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <MapPin className="h-4 w-4 flex-shrink-0" />
              {locationError}
            </div>
          )}

          {!capturedImage ? (
            <div className="relative">
              <div className="relative rounded-xl overflow-hidden bg-gray-900 aspect-[3/4]">
                {cameraError ? (
                  <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-white">
                    <div>
                      <Camera className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">{cameraError}</p>
                      <button onClick={startCamera} className="mt-3 text-teal-400 underline text-sm">
                        Retry
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                      style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
                    />
                    {/* Oval face guide overlay */}
                    <div className="absolute inset-0 pointer-events-none">
                      <svg className="w-full h-full" viewBox="0 0 300 400" preserveAspectRatio="xMidYMid slice">
                        <defs>
                          <mask id="face-mask">
                            <rect width="300" height="400" fill="white" />
                            <ellipse cx="150" cy="170" rx="75" ry="100" fill="black" />
                          </mask>
                        </defs>
                        <rect width="300" height="400" fill="rgba(0,0,0,0.45)" mask="url(#face-mask)" />
                        <ellipse cx="150" cy="170" rx="75" ry="100" fill="none" stroke="#14b8a6" strokeWidth="2.5" strokeDasharray="8 4" />
                      </svg>
                    </div>
                    <div className="absolute bottom-3 left-0 right-0 text-center">
                      <span className="text-white/80 text-xs bg-black/50 px-3 py-1 rounded-full">
                        Position your face in the oval
                      </span>
                    </div>
                  </>
                )}
              </div>
              <canvas ref={canvasRef} className="hidden" />

              <div className="flex items-center justify-center gap-4 mt-4">
                <button
                  onClick={() => setFacingMode((m) => m === 'user' ? 'environment' : 'user')}
                  className="p-3 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                  title="Switch camera"
                >
                  <RotateCcw className="h-5 w-5" />
                </button>
                <button
                  onClick={capturePhoto}
                  disabled={!cameraActive || !selectedStaff}
                  className="px-8 py-3 rounded-full bg-teal-600 text-white font-semibold
                             hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed
                             transition shadow-lg flex items-center gap-2"
                >
                  <Camera className="h-5 w-5" />
                  Take Selfie
                </button>
              </div>
              {!selectedStaff && (
                <p className="text-center text-xs text-amber-600 mt-2">Please select a staff member first</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl overflow-hidden border-2 border-teal-200">
                <img
                  src={capturedImage}
                  alt="Captured selfie"
                  className="w-full aspect-[3/4] object-cover"
                  style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={retake}
                  disabled={submitting}
                  className="flex-1 px-4 py-3 rounded-xl border border-gray-300
                             text-ga-text-primary font-medium hover:bg-gray-50
                             disabled:opacity-40 transition flex items-center justify-center gap-2"
                >
                  <RotateCcw className="h-4 w-4" />
                  Retake
                </button>
                <button
                  onClick={submitPhoto}
                  disabled={submitting}
                  className="flex-1 px-4 py-3 rounded-xl bg-teal-600 text-white
                             font-semibold hover:bg-teal-700 disabled:opacity-40
                             transition shadow flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Submit
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
